import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertTriangle, ArrowLeft, Gavel, Loader2, Users, Trophy } from 'lucide-react';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import { fetchWithAuth } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import './PerformanceStats.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

// Define interfaces
interface DebateStats {
  score_progression: {
    labels: string[];
    scores: number[];
  };
  speaking_time_progression: {
    labels: string[];
    times: string[];
    seconds: number[];
  };
  roles_distribution: Record<string, number>;
  win_loss: {
    wins: number;
    losses: number;
    total_debates: number;
  };
  total_debates: number; // Make this required, not optional
  avg_score?: number; // Add this
}

interface AdjudicationStats {
  rating_progression: {
    labels: string[];
    ratings: number[];
  };
  roles_distribution: Record<string, number>;
  total_rounds: number;
  avg_rating?: number; // Add this
}

// Add this utility function to transform debate history data to performance stats format
const transformDebateHistoryToStats = (historyData: any) => {
  if (!historyData) {
    console.log("No history data to transform");
    return null;
  }
  
  console.log("Transforming history data:", historyData);
  
  // Initialize result structure with properly typed arrays and objects
  const result: {
    debate_stats: {
      score_progression: { 
        labels: string[], 
        scores: number[] 
      },
      speaking_time_progression: {
        labels: string[],
        times: string[],
        seconds: number[]
      },
      roles_distribution: Record<string, number>,
      win_loss: {
        wins: number,
        losses: number,
        total_debates: number
      },
      total_debates: number,
      avg_score?: number
    },
    adjudication_stats: {
      rating_progression: { 
        labels: string[], 
        ratings: number[] 
      },
      roles_distribution: Record<string, number>,
      total_rounds: number,
      avg_rating?: number
    }
  } = {
    debate_stats: {
      score_progression: { 
        labels: [], 
        scores: [] 
      },
      speaking_time_progression: {
        labels: [],
        times: [],
        seconds: []
      },
      roles_distribution: {},
      win_loss: {
        wins: 0,
        losses: 0,
        total_debates: 0
      },
      total_debates: 0
    },
    adjudication_stats: {
      rating_progression: { 
        labels: [], 
        ratings: [] 
      },
      roles_distribution: {},
      total_rounds: 0
    }
  };
  
  // Check if user_performance is available (from user_debate_history_simple)
  if (historyData.user_performance) {
    const userPerf = historyData.user_performance;
    
    // Use the calculated average score if available
    if (userPerf.avg_speaker_score !== undefined) {
      result.debate_stats.avg_score = userPerf.avg_speaker_score;
    }
    
    // Use the roles distribution if available
    if (userPerf.roles) {
      result.debate_stats.roles_distribution = userPerf.roles;
    }
    
    // Use the win/loss data if available
    if (userPerf.win_loss) {
      result.debate_stats.win_loss.wins = userPerf.win_loss.wins || 0;
      result.debate_stats.win_loss.losses = userPerf.win_loss.losses || 0;
      result.debate_stats.win_loss.total_debates = 
        (userPerf.win_loss.wins || 0) + (userPerf.win_loss.losses || 0);
    }
    
    // Use the total debates if available
    if (userPerf.total_debates !== undefined) {
      result.debate_stats.total_debates = userPerf.total_debates;
    }
  }
  
  // Process debate history if available
  if (Array.isArray(historyData?.debate_history)) {
    try {
      // Sort by date descending to get most recent first
      const sortedDebates = [...historyData.debate_history].sort((a, b) => {
        const dateA = a?.completed_at ? new Date(a.completed_at).getTime() : 0;
        const dateB = b?.completed_at ? new Date(b.completed_at).getTime() : 0;
        return dateB - dateA;
      });
      
      // If total_debates isn't set from user_performance, set it from history length
      if (!result.debate_stats.total_debates) {
        result.debate_stats.total_debates = sortedDebates.length;
      }
      
      // Process each debate for stats
      sortedDebates.forEach(debate => {
        if (!debate) return; // Skip null or undefined entries
        
        // Add to score progression (reversed to show chronological order)
        if (debate.completed_at && debate.speaker_score) {
          result.debate_stats.score_progression.labels.unshift(
            new Date(debate.completed_at).toLocaleDateString()
          );
          result.debate_stats.score_progression.scores.unshift(
            debate.speaker_score || 0
          );
        }
        
        // Process speaking time data
        if (debate.completed_at && debate.speaking_time) {
          // Add to speaking time progression
          result.debate_stats.speaking_time_progression.labels.unshift(
            new Date(debate.completed_at).toLocaleDateString()
          );
          result.debate_stats.speaking_time_progression.times.unshift(
            debate.speaking_time || '00:00'
          );
          
          // Convert time format (MM:SS) to seconds for chart
          const timeParts = (debate.speaking_time || '00:00').split(':');
          let seconds = 0;
          if (timeParts.length === 2) {
            seconds = (parseInt(timeParts[0]) * 60) + parseInt(timeParts[1]);
          }
          result.debate_stats.speaking_time_progression.seconds.unshift(seconds);
        }
        
        // Count role distribution - always add to the distribution regardless of existing data
        if (debate.user_role) {
          if (!result.debate_stats.roles_distribution[debate.user_role]) {
            result.debate_stats.roles_distribution[debate.user_role] = 0;
          }
          result.debate_stats.roles_distribution[debate.user_role]++;
        }
        
        // Count wins/losses if not already populated from user_performance
        if (debate.result && 
            result.debate_stats.win_loss.wins === 0 && 
            result.debate_stats.win_loss.losses === 0) {
          if (debate.result === 'Won') {
            result.debate_stats.win_loss.wins++;
          } else if (debate.result === 'Lost') {
            result.debate_stats.win_loss.losses++;
          }
        }
      });

      // Update total debates in win_loss too for consistency
      if (result.debate_stats.win_loss.total_debates === 0) {
        result.debate_stats.win_loss.total_debates = 
          result.debate_stats.win_loss.wins + result.debate_stats.win_loss.losses;
      }

      // Calculate average score if not already provided
      if (result.debate_stats.avg_score === undefined && result.debate_stats.score_progression.scores.length > 0) {
        const sum = result.debate_stats.score_progression.scores.reduce((a, b) => a + b, 0);
        result.debate_stats.avg_score = parseFloat((sum / result.debate_stats.score_progression.scores.length).toFixed(1));
      }
      
      // Ensure we have an avg_score value for display if there are any debates at all
      if (result.debate_stats.avg_score === undefined && result.debate_stats.total_debates > 0) {
        // Use a reasonable default if we have debates but no scores
        result.debate_stats.avg_score = 75.0;
      }
    } catch (error) {
      console.error("Error processing debate history:", error);
    }
  }
  
  // Process adjudication history if available
  if (Array.isArray(historyData?.adjudication_history)) {
    try {
      // Sort by date descending
      const sortedAdjudications = [...historyData.adjudication_history].sort((a, b) => {
        const dateA = a?.completed_at ? new Date(a.completed_at).getTime() : 0;
        const dateB = b?.completed_at ? new Date(b.completed_at).getTime() : 0;
        return dateB - dateA;
      });
      
      result.adjudication_stats.total_rounds = sortedAdjudications.length;
      
      // Process each adjudication for stats
      sortedAdjudications.forEach(adj => {
        if (!adj) return; // Skip null or undefined entries
        
        // Add to rating progression (reversed for chronological order)
        if (adj.completed_at) {
          result.adjudication_stats.rating_progression.labels.unshift(
            new Date(adj.completed_at).toLocaleDateString()
          );
          result.adjudication_stats.rating_progression.ratings.unshift(
            adj.user_rating || 0
          );
        }
        
        // Count role distribution
        if (adj.user_role) {
          if (!result.adjudication_stats.roles_distribution[adj.user_role]) {
            result.adjudication_stats.roles_distribution[adj.user_role] = 0;
          }
          result.adjudication_stats.roles_distribution[adj.user_role]++;
        }
      });

      // Calculate average rating
      if (result.adjudication_stats.rating_progression.ratings.length > 0) {
        const sum = result.adjudication_stats.rating_progression.ratings.reduce((a, b) => a + b, 0);
        result.adjudication_stats.avg_rating = parseFloat((sum / result.adjudication_stats.rating_progression.ratings.length).toFixed(1));
      }
    } catch (error) {
      console.error("Error processing adjudication history:", error);
    }
  }
  
  return result;
};

// Similar to what worked in DebateHistory, let's add a function to get a valid user ID
const getValidUserId = async (): Promise<string | null> => {
  try {
    // First check localStorage
    const storedUserId = localStorage.getItem('userId');
    
    if (storedUserId) {
      console.log(`Found stored userId: ${storedUserId}`);
      return storedUserId;
    }
    
    // Try numeric ID backup
    const numericId = localStorage.getItem('userIdNumeric');
    if (numericId) {
      console.log(`Found numeric userId: ${numericId}`);
      return numericId;
    }
    
    // If no userId in localStorage, try to fetch it from profile
    console.log("No userId in localStorage, trying to fetch from profile");
    
    // Try multiple endpoints for getting user profile
    const endpoints = [
      '/api/users/me/',
      '/api/user/profile/',
      '/users/me/',
      '/api/users/current/',
      '/users/profile/'
    ];
    
    for (const endpoint of endpoints) {
      try {
        const userData = await fetchWithAuth(endpoint);
        if (userData && userData.id) {
          console.log(`Retrieved userId from ${endpoint}: ${userData.id}`);
          localStorage.setItem('userId', userData.id.toString());
          return userData.id.toString();
        }
      } catch (err) {
        console.log(`Failed to fetch user profile from ${endpoint}`);
      }
    }
    
    // Last resort - try to use username
    const username = localStorage.getItem('username');
    if (username) {
      console.log(`No user ID found, using username: ${username}`);
      return username;
    }
    
    return null;
  } catch (err) {
    console.error("Failed to get valid user ID:", err);
    return null;
  }
};

const PerformanceStats: React.FC = () => {
  const navigate = useNavigate();
  const { pageTransition } = useTheme();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("debate");
  
  const [debateStats, setDebateStats] = useState<DebateStats | null>(null);
  const [adjudicationStats, setAdjudicationStats] = useState<AdjudicationStats | null>(null);
  
  // Wrap fetchPerformanceStats with useCallback to prevent it from being recreated on every render
  const fetchPerformanceStats = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      // Get a valid user ID first
      const userId = await getValidUserId();
      console.log(`Using userId for performance stats: ${userId}`);
      
      if (!userId) {
        console.warn("Could not obtain a valid user ID");
        setError("User identification failed. Please log in again.");
        return;
      }
      
      // Only use the known working endpoint
      const endpoint = `/api/users/debate-history/simple/?userId=${userId}`;
      
      try {
        console.log(`Using the known working endpoint: ${endpoint}`);
        const data = await fetchWithAuth(endpoint);
        
        if (data) {
          console.log(`Got data from ${endpoint}:`, data);
          
          // Transform debate history to stats
          const statsData = transformDebateHistoryToStats(data);
          if (statsData) {
            console.log("Transformed data:", statsData);
            setDebateStats(statsData.debate_stats);
            setAdjudicationStats(statsData.adjudication_stats);
          } else {
            throw new Error("Failed to transform data");
          }
        } else {
          throw new Error("No data returned from endpoint");
        }
      } catch (error) {
        console.error(`Endpoint ${endpoint} failed:`, error);
        
        // Create default sample data if endpoint fails
        console.log("Endpoint failed, creating minimal fallback data");
        setDebateStats({
          score_progression: {
            labels: [],
            scores: []
          },
          speaking_time_progression: {
            labels: [],
            times: [],
            seconds: []
          },
          roles_distribution: {},
          win_loss: {
            wins: 0,
            losses: 0,
            total_debates: 0
          },
          total_debates: 0,
          avg_score: 0
        });
        
        setAdjudicationStats({
          rating_progression: {
            labels: [],
            ratings: []
          },
          roles_distribution: {},
          total_rounds: 0,
          avg_rating: 0
        });
      }
    } catch (err: any) {
      console.error("Error loading performance stats:", err);
      setError(err.message || "Failed to load performance statistics");
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch stats data when component mounts
  useEffect(() => {
    fetchPerformanceStats();
  }, [fetchPerformanceStats]); // Add fetchPerformanceStats to the dependency array

  // Handle going back to dashboard
  const handleBack = () => {
    pageTransition(() => {
      navigate('/dashboard');
    });
  };
  
  // Prepare line chart data for debate scores
  const debateScoreData = {
    labels: debateStats?.score_progression?.labels || [],
    datasets: [
      {
        label: 'Speaker Score',
        data: debateStats?.score_progression?.scores || [],
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
        tension: 0.3,
      },
    ],
  };
  
  // Prepare line chart options for debate scores
  const debateLineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Speaker Score Progression',
      },
    },
    scales: {
      y: {
        min: 65,
        max: 85,
        title: {
          display: true,
          text: 'Score'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Rounds'
        }
      }
    },
  };
  
  // Prepare pie chart data for debate roles
  const debateRolesData = {
    labels: Object.keys(debateStats?.roles_distribution || {}),
    datasets: [
      {
        data: Object.values(debateStats?.roles_distribution || {}),
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(255, 129, 102, 0.7)',
          'rgba(56, 198, 139, 0.7)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 129, 102, 1)',
          'rgba(56, 198, 139, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };
  
  // Prepare line chart data for adjudication ratings
  const adjudicationRatingData = {
    labels: adjudicationStats?.rating_progression?.labels || [],
    datasets: [
      {
        label: 'Adjudication Rating',
        data: adjudicationStats?.rating_progression?.ratings || [],
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.5)',
        tension: 0.3,
      },
    ],
  };
  
  // Prepare line chart options for adjudication ratings
  const adjudicationLineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Adjudication Rating Progression',
      },
    },
    scales: {
      y: {
        min: 0,
        max: 10,
        title: {
          display: true,
          text: 'Rating'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Rounds'
        }
      }
    },
  };
  
  // Prepare pie chart data for adjudication roles
  const adjudicationRolesData = {
    labels: Object.keys(adjudicationStats?.roles_distribution || {}),
    datasets: [
      {
        data: Object.values(adjudicationStats?.roles_distribution || {}),
        backgroundColor: [
          'rgba(255, 159, 64, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(153, 102, 255, 0.7)',
        ],
        borderColor: [
          'rgba(255, 159, 64, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Prepare line chart data for speaking time
  const speakingTimeData = {
    labels: debateStats?.speaking_time_progression?.labels || [],
    datasets: [
      {
        label: 'Speaking Time (seconds)',
        data: debateStats?.speaking_time_progression?.seconds || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.3,
      },
    ],
  };

  // Prepare line chart options for speaking time
  const speakingTimeOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Speaking Time Progression',
      },
    },
    scales: {
      y: {
        min: 0,
        max: 600,
        title: {
          display: true,
          text: 'Time (seconds)'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Rounds'
        }
      }
    },
  };

  // Add this helper function
  const getMostFrequentRole = (roles: Record<string, number>): string => {
    if (Object.keys(roles).length === 0) return "None";
    return Object.entries(roles).sort((a, b) => b[1] - a[1])[0][0];
  };

  return (
    <div className="p-4 md:p-6 w-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Performance Statistics</h1>
          <p className="text-gray-500 dark:text-gray-400">View your debate and adjudication performance</p>
        </div>
        
        <Button 
          variant="outline"
          onClick={handleBack}
          className="flex items-center gap-1"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </Button>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin">
            <Loader2 className="h-8 w-8 text-primary" />
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full md:w-auto flex">
              <TabsTrigger value="debate" className="flex-1 md:flex-initial">
                <Users size={16} />
                <span className="ml-2">As Debater</span>
              </TabsTrigger>
              <TabsTrigger value="adjudication" className="flex-1 md:flex-initial">
                <Trophy size={16} />
                <span className="ml-2">As Adjudicator</span>
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="debate" className="space-y-6">
              {debateStats ? (
                <>
                  {/* Stats Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Total Debates</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {debateStats.total_debates || 0}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Win Rate</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {debateStats.win_loss?.total_debates 
                            ? Math.round((debateStats.win_loss.wins / debateStats.win_loss.total_debates) * 100) 
                            : 0}%
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {debateStats.win_loss?.wins || 0} wins, {debateStats.win_loss?.losses || 0} losses
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Avg. Speaker Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {debateStats.avg_score ? debateStats.avg_score.toFixed(1) : 
                           debateStats.total_debates > 0 ? "75.0" : "0.0"}
                        </div>
                        {debateStats.total_debates > 0 && !debateStats.avg_score && (
                          <p className="text-xs text-muted-foreground">Estimated</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Charts Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Line Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Speaker Score Trend</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {/* Modified condition to focus on actual progress data */}
                        {debateStats.score_progression?.scores?.length > 0 ? (
                          <div className="h-80">
                            <Line data={debateScoreData} options={debateLineOptions} />
                          </div>
                        ) : debateStats.total_debates > 0 ? (
                          <div className="h-full flex flex-col items-center justify-center">
                            {/* Show a basic chart with placeholder data only as fallback */}
                            <Line 
                              data={{
                                labels: ['Your Average'],
                                datasets: [{
                                  label: 'Speaker Score',
                                  data: [debateStats.avg_score || 75],
                                  borderColor: 'rgb(53, 162, 235)',
                                  backgroundColor: 'rgba(53, 162, 235, 0.5)',
                                }]
                              }} 
                              options={{
                                ...debateLineOptions,
                                plugins: {
                                  ...debateLineOptions.plugins,
                                  title: {
                                    ...debateLineOptions.plugins.title,
                                    text: 'Average Speaker Score'
                                  }
                                }
                              }}
                            />
                            <p className="text-sm text-muted-foreground mt-2">
                              No round scores available - showing average only
                            </p>
                          </div>
                        ) : (
                          <div className="flex h-80 items-center justify-center text-gray-400">
                            <p>No score data available</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Pie Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Role Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(debateStats.roles_distribution || {}).length > 0 ? (
                          <div className="h-80">
                            <Pie data={debateRolesData} />
                          </div>
                        ) : (
                          <div className="flex h-80 items-center justify-center text-gray-400">
                            <p>No role data available</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* New Speaking Time Chart Row */}
                  <div className="grid grid-cols-1 gap-6">
                    {/* Line Chart - Speaking Time */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Speaking Time Trend</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {debateStats.speaking_time_progression?.seconds?.length > 0 ? (
                          <div className="h-80">
                            <Line data={speakingTimeData} options={speakingTimeOptions} />
                          </div>
                        ) : (
                          <div className="flex h-80 items-center justify-center text-gray-400">
                            <p>No speaking time data available</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              ) : (
                <div className="text-center p-12">
                  <h3 className="text-lg font-medium mb-2">No Debate Data Available</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Participate in debates to start seeing your performance statistics here.
                  </p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="adjudication" className="space-y-6">
              {adjudicationStats ? (
                <>
                  {/* Stats Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Total Rounds</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {adjudicationStats?.total_rounds || 0}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Chair Experience</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {adjudicationStats.roles_distribution?.['Chair Adjudicator'] || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">rounds as Chair</p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Avg. Rating</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {adjudicationStats.avg_rating ? adjudicationStats.avg_rating.toFixed(1) : "0.0"}
                        </div>
                        <p className="text-sm text-gray-500">Scale: 1-10</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle>Primary Role</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {getMostFrequentRole(adjudicationStats?.roles_distribution || {})}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Charts Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Line Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Rating Trend</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {adjudicationStats.rating_progression?.ratings?.length > 0 ? (
                          <div className="h-80">
                            <Line data={adjudicationRatingData} options={adjudicationLineOptions} />
                          </div>
                        ) : (
                          <div className="flex h-80 items-center justify-center text-gray-400">
                            <p>No rating data available</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Pie Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Role Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(adjudicationStats.roles_distribution || {}).length > 0 ? (
                          <div className="h-80">
                            <Pie data={adjudicationRolesData} />
                          </div>
                        ) : (
                          <div className="flex h-80 items-center justify-center text-gray-400">
                            <p>No role data available</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              ) : (
                <div className="text-center p-12">
                  <h3 className="text-lg font-medium mb-2">No Adjudication Data Available</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Adjudicate rounds to start seeing your performance statistics here.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default PerformanceStats;